/**
 * TelefonGirdisi.jsx — ülke arama kodu seçicisi + ulusal numara kutusu (backlog #746). Mantık: telefon.js.
 *
 * Eskiden her telefon alanı serbest bir metin kutusuydu; SMS özelliği numaranın tek biçimde olmasını ister. Burada kod (+90 varsayılan,
 * Türkçe/İngilizce ülke adı ya da koda göre aranır) ve numara ayrı kutulardadır; numara kutusu yalnızca rakam alır ve uzunluğu koda uyar
 * (+90: 10 hane, baştaki 0 atılır; diğerleri: kod + numara en çok 15). "+90 532 306 49 70", "0532 ..." ya da "0090 ..." yapıştırılırsa kod
 * ile numaraya kendiliğinden bölünür.
 *
 * `onChange(değer)` — yerel olay değil, TEK metin gelir, arka ucun sakladığı biçimde: +90 için 10 hane ("5323064970"), başka kod için
 * "+491701234567"; boş kutu "". Arka uç (#633) bunu olduğu gibi kabul eder.
 *
 * ESKİ NUMARA: çözülemeyen kayıtlı bir değer olduğu gibi gösterilir ("standart biçimde değil, lütfen düzeltin") ve biri kutuya yazmadıkça
 * `onChange` hiç çağrılmaz — yani başka bir alan düzenlenirken eski değer aynen geri gider, arka uç değişmemiş değeri reddetmez.
 * Çağıran `value`'yu yalnızca bu `onChange`'in verdiği değerle ya da yeni bir kayıt yüklerken değiştirmelidir.
 */
import React, { useMemo, useState } from "react";
import Select from "react-select";
import { Input } from "reactstrap";
import { useTranslation } from "react-i18next";
import { ULKE_KODLARI } from "./ulkeTelefonKodlari";
import { telefonCoz, telefonBirlestir, numaraGirildi, kodSecildi, telefonDurumu, TR_HANE, TR_KODU } from "./telefon";

// Aramada Türkçe/İngilizce fark etmesin: küçült, "ı"→"i", aksanları at ("Çin" = "cin", "Türkiye" = "turkiye").
const normalle = (s) => String(s).toLocaleLowerCase("tr").replace(/ı/g, "i").normalize("NFD").replace(/[̀-ͯ]/g, "");

const adlandirici = (dil) => { try { return new Intl.DisplayNames([dil], { type: "region" }); } catch { return null; } };
const adi = (dn, iso) => { try { return dn?.of(iso) || iso; } catch { return iso; } };

// dil → seçenekler. Ad gösterilen dilde; arama her iki dilde (tr + en) ve kodla (+49 / 49) çalışır.
const SECENEKLER = new Map();
function secenekler(dil) {
  if (!SECENEKLER.has(dil)) {
    const goster = adlandirici(dil), tr = adlandirici("tr"), en = adlandirici("en");
    const liste = ULKE_KODLARI.map(([iso, kod]) => {
      const ad = adi(goster, iso);
      return { iso, kod, ad, arama: normalle([ad, adi(tr, iso), adi(en, iso), iso === "TR" ? "turkey" : "", iso, `+${kod}`].join(" ")) };
    });
    const [turkiye, ...digerleri] = liste; // Türkiye her zaman ilk
    digerleri.sort((a, b) => a.ad.localeCompare(b.ad, dil));
    SECENEKLER.set(dil, [turkiye, ...digerleri]);
  }
  return SECENEKLER.get(dil);
}

const filtre = (secenek, girdi) => !girdi || secenek.data.arama.includes(normalle(girdi));

// Tema değişkenleri (UlkeSecici ile aynı): koyu temada seçici beyaz kalmasın.
const stiller = (kucuk) => ({
  container: (b) => ({ ...b, flex: "0 0 auto", width: kucuk ? 86 : 94 }),
  control: (b, s) => ({
    ...b, minHeight: kucuk ? 31 : 38, fontSize: kucuk ? ".8125rem" : undefined,
    backgroundColor: "var(--vz-input-bg-custom, var(--vz-secondary-bg, #fff))",
    borderColor: s.isFocused ? "var(--vz-primary, #405189)" : "var(--vz-input-border-custom, var(--vz-border-color, #ced4da))",
    boxShadow: "none",
  }),
  valueContainer: (b) => ({ ...b, padding: "0 4px 0 8px" }),
  singleValue: (b) => ({ ...b, color: "var(--vz-body-color, inherit)" }),
  input: (b) => ({ ...b, color: "var(--vz-body-color, inherit)" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (b) => ({ ...b, padding: 4 }),
  menu: (b) => ({ ...b, width: 280, backgroundColor: "var(--vz-secondary-bg, #fff)", fontSize: ".8125rem" }),
  option: (b, s) => ({
    ...b, color: s.isSelected ? "#fff" : "var(--vz-body-color, inherit)",
    backgroundColor: s.isSelected ? "var(--vz-primary, #405189)" : s.isFocused ? "var(--vz-tertiary-bg, #f3f6f9)" : "transparent",
  }),
  menuPortal: (b) => ({ ...b, zIndex: 2000 }), // modalın (1055) üstünde
});

export default function TelefonGirdisi({ value, onChange, id, disabled = false, invalid = false, bsSize, className, placeholder }) {
  const { t, i18n } = useTranslation("common");
  const dil = (i18n?.language || "tr").slice(0, 2);
  const liste = useMemo(() => secenekler(dil), [dil]);
  const kucuk = bsSize === "sm";
  const stil = useMemo(() => stiller(kucuk), [kucuk]);

  // Kod + numara iç durumda tutulur (boş numarayla seçilen kod, çağıranın "" değerinden geri çıkarılamaz). Dışarıdan farklı bir değer
  // gelirse (kayıt yüklendi, form sıfırlandı) yeniden çözülür; kendi verdiğimiz değer dönerse durum korunur.
  const [d, setD] = useState(() => telefonCoz(value));
  const [onceki, setOnceki] = useState(value);
  const [birakildi, setBirakildi] = useState(false);
  if (value !== onceki) {
    setOnceki(value);
    if ((value ?? "") !== telefonBirlestir(d)) setD(telefonCoz(value));
  }

  const degistir = (yeni) => { setD(yeni); onChange?.(telefonBirlestir(yeni)); };
  const durum = telefonDurumu(d);
  const eksik = durum === "eksik";

  return (
    <div className={className}>
      <div className="d-flex gap-1">
        <Select
          inputId={id ? `${id}-kod` : undefined}
          aria-label={t("telefon.ulkeKodu")}
          classNamePrefix="telefon-kodu"
          options={liste}
          value={liste.find((o) => o.iso === d.iso) || liste[0]}
          onChange={(o) => o && degistir(kodSecildi(d, o.iso, o.kod))}
          getOptionValue={(o) => o.iso}
          filterOption={filtre}
          formatOptionLabel={(o, { context }) => (context === "value" ? `+${o.kod}` : (
            <span className="d-flex justify-content-between gap-2"><span>{o.ad}</span><span style={{ opacity: 0.7 }}>+{o.kod}</span></span>
          ))}
          isDisabled={disabled}
          noOptionsMessage={() => t("telefon.ulkeBulunamadi")}
          styles={stil}
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
          menuPlacement="auto"
        />
        <Input
          id={id} type="tel" inputMode="tel" autoComplete="tel-national" bsSize={bsSize} className="flex-grow-1" style={{ minWidth: 0 }}
          value={d.eski != null ? d.eski : d.ulusal} disabled={disabled} invalid={invalid || (birakildi && eksik)}
          placeholder={placeholder ?? (d.kod === TR_KODU ? "5xx xxx xx xx" : t("telefon.numara"))}
          onChange={(e) => degistir(numaraGirildi(d, e.target.value))} onBlur={() => setBirakildi(true)}
        />
      </div>
      {durum === "eski" && <div className="text-warning small mt-1">{t("telefon.standartDegil")}</div>}
      {birakildi && eksik && (
        <div className="text-danger small mt-1">{d.kod === TR_KODU ? t("telefon.haneliOlmali", { n: TR_HANE }) : t("telefon.cokKisa")}</div>
      )}
    </div>
  );
}
